import styles from "./AdminArticles.module.scss";
import { useQuery } from "urql";
import { useState, useEffect, useMemo, useCallback } from "react";
import type {
  CellContext,
  PaginationState,
  SortingState,
} from "@tanstack/react-table";
import { createColumnHelper } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { graphql, getFragmentData } from "../../../__generated__/gql";
import { Search } from "../../../ui/Icon/Icon";
import Table from "../../../ui/Table/Table";
import type {
  AdminArticles_AdminArticleFragment,
  ArticleSort,
  ArticleStatusFilter,
  ArticleTagFilter,
  ArticleTypeFilter,
  DateFilter,
  StringFilter,
} from "../../../__generated__/gql/graphql";
import {
  ArticleSortField,
  ArticleStatus,
  ArticleType,
  SortOrder,
} from "../../../__generated__/gql/graphql";
import urls from "../../../routes";
import ToggleGroup from "../../../ui/ToggleGroup/ToggleGroup";
import Truncate from "../../../ui/Truncate/Truncate";
import FilterDrawer from "../../../ui/FilterDrawer/FilterDrawer";
import FilterButton from "../../../ui/FilterDrawer/FilterButton";
import useFilters, {
  getGraphQlDateFilter,
  getGraphQlStringFilter,
} from "../../../ui/FilterDrawer/useFilters";
import FormattedDate from "../../../ui/FormattedDate/FormattedDate";
import Avatar from "../../../ui/Avatar/Avatar";
import TextInput from "../../../ui/TextInput/TextInput";
import { useDisclosure } from "../../../ui/util/useDisclosure";
import StatusBadge from "../../../components/StatusBadge/StatusBadge";
import Badge from "../../../ui/Badge/Badge";
import toSentenceCase from "../../../util/toSentenceCase";

const AdminArticleFragment = graphql(/* GraphQL */ `
  fragment AdminArticles_AdminArticle on AdminArticle {
    id
    title
    type
    keyword
    status
    processingMessage
    metaDescription
    excerpt
    additionalPrompt
    createdAt
    updatedAt
    publishedAt
    generatedAt
    language
    tag {
      id
      name
    }
    tenant {
      id
      name
    }
    createdBy {
      id
      status
      firstName
      lastName
      email
      imageUrl
    }
    ...StatusBadge
  }
`);

const AdminArticlesQuery = graphql(/* GraphQL */ `
  query adminArticles(
    $filters: [ArticleFilter!]
    $paginationOptions: OffsetPaginationOptions
    $sort: ArticleSort
  ) {
    adminArticles(
      filters: $filters
      paginationOptions: $paginationOptions
      sort: $sort
    ) {
      nodes {
        ...AdminArticles_AdminArticle
      }
      totalCount
    }

    tags {
      id
      name
    }
  }
`);

function ArticleCell({
  row,
}: CellContext<AdminArticles_AdminArticleFragment, unknown>) {
  return (
    <div className={styles.articleCellContainer}>
      <Link
        className={styles.articleCell}
        to={urls.admin.article(row.original.id)}
      >
        {row.original.title ||
        ![ArticleStatus.Processing, ArticleStatus.Draft].includes(
          row.original.status,
        ) ? (
          <Truncate className={styles.articleTitle}>
            {row.original.title || "-"}
          </Truncate>
        ) : (
          <div>
            <Badge
              className={styles.articleTitleLoader}
              variant="pill"
              color="default"
              size="small"
              showDot={false}
              loading
            />
          </div>
        )}
        {/* <span className={styles.articleType}>
          {row.original.type ? formatArticleType(row.original.type) : "-"}
        </span> */}
      </Link>
    </div>
  );
}

function KeywordCell({
  cell,
}: CellContext<AdminArticles_AdminArticleFragment, string>) {
  return <Truncate>{cell.getValue()}</Truncate>;
}

function StatusCell({
  cell,
}: CellContext<AdminArticles_AdminArticleFragment, ArticleStatus>) {
  return <StatusBadge data={cell.row.original} />;
}

function UserCell({
  cell,
}: CellContext<
  AdminArticles_AdminArticleFragment,
  AdminArticles_AdminArticleFragment["createdBy"]
>) {
  const user = cell.getValue();

  if (!user || !user.firstName || !user.lastName || !user.imageUrl) {
    return null;
  }

  return (
    <div className={styles.user}>
      <Avatar
        size="xsmall"
        imageUrl={user.imageUrl}
        firstName={user.firstName}
        lastName={user.lastName}
        email={user.email}
        showName={{ firstName: true }}
      />
    </div>
  );
}

const DEFAULT_PAGINATION_STATE: PaginationState = {
  pageIndex: 0,
  pageSize: 25,
};

function AdminArticles() {
  const { filterGroups, deleteFilter, addFilter, editFilter, clearFilters } =
    useFilters({});
  const [isFiltersDrawerOpen, { open, close }] = useDisclosure();
  const [keyPhrase, setKeyPhrase] = useState<string>("");
  const [debouncedKeyPhrase, setDebouncedKeyPhrase] = useState<string>("");
  const [selectedTableView, setSelectedTableView] = useState("simple");
  const [pagination, setPagination] = useState<PaginationState>(
    DEFAULT_PAGINATION_STATE,
  );
  const [articleSort, setArticleSort] = useState<ArticleSort>();

  const queryFilter = useMemo(() => {
    const baseFilters = {
      keyphrase: debouncedKeyPhrase,
    };

    if (!filterGroups.length) {
      return [baseFilters];
    }

    return filterGroups.map((filters) => {
      const keywordFilters: StringFilter[] = [];
      const titleFilters: StringFilter[] = [];
      const bodyFilters: StringFilter[] = [];
      const metaDescriptionFilters: StringFilter[] = [];
      const excerptFilters: StringFilter[] = [];
      const additionalPromptFilters: StringFilter[] = [];
      const tagFilters: ArticleTagFilter[] = [];
      const typeFilters: ArticleTypeFilter[] = [];
      const createdAtFilters: DateFilter[] = [];
      const updatedAtFilters: DateFilter[] = [];
      const generatedAtFilters: DateFilter[] = [];
      const statusFilters: ArticleStatusFilter[] = [];

      filters.forEach((filter) => {
        if (filter.type === "enum") {
          if (filter.configuration.name === "tag") {
            tagFilters.push({
              includes:
                filter.filterType === "IS_ANY_OF"
                  ? filter.values.map((value) => value.value)
                  : [],
              notIncludes:
                filter.filterType === "IS_NONE_OF"
                  ? filter.values.map((value) => value.value)
                  : [],
            });
          }

          if (filter.configuration.name === "status") {
            statusFilters.push({
              includes:
                filter.filterType === "IS_ANY_OF"
                  ? (filter.values.map(
                      (value) => value.value,
                    ) as ArticleStatus[])
                  : undefined,
              notIncludes:
                filter.filterType === "IS_NONE_OF"
                  ? (filter.values.map(
                      (value) => value.value,
                    ) as ArticleStatus[])
                  : undefined,
            });
          }

          if (filter.configuration.name === "type") {
            typeFilters.push({
              includes:
                filter.filterType === "IS_ANY_OF"
                  ? (filter.values.map((value) => value.value) as ArticleType[])
                  : undefined,
              notIncludes:
                filter.filterType === "IS_NONE_OF"
                  ? (filter.values.map((value) => value.value) as ArticleType[])
                  : undefined,
            });
          }
        }

        if (filter.type === "string") {
          if (filter.configuration.name === "keyword") {
            keywordFilters.push(getGraphQlStringFilter(filter));
          }

          if (filter.configuration.name === "title") {
            titleFilters.push(getGraphQlStringFilter(filter));
          }

          if (filter.configuration.name === "body") {
            bodyFilters.push(getGraphQlStringFilter(filter));
          }

          if (filter.configuration.name === "metaDescription") {
            metaDescriptionFilters.push(getGraphQlStringFilter(filter));
          }

          if (filter.configuration.name === "excerpt") {
            excerptFilters.push(getGraphQlStringFilter(filter));
          }

          if (filter.configuration.name === "additionalPrompt") {
            additionalPromptFilters.push(getGraphQlStringFilter(filter));
          }
        }

        if (filter.type === "date") {
          if (filter.configuration.name === "createdAt") {
            createdAtFilters.push(getGraphQlDateFilter(filter));
          }

          if (filter.configuration.name === "updatedAt") {
            updatedAtFilters.push(getGraphQlDateFilter(filter));
          }

          if (filter.configuration.name === "generatedAt") {
            generatedAtFilters.push(getGraphQlDateFilter(filter));
          }
        }
      });
      return {
        ...baseFilters,
        tag: tagFilters,
        status: statusFilters,
        keyword: keywordFilters,
        title: titleFilters,
        body: bodyFilters,
        metaDescription: metaDescriptionFilters,
        excerpt: excerptFilters,
        additionalPrompt: additionalPromptFilters,
        type: typeFilters,
        createdAt: createdAtFilters,
        updatedAt: updatedAtFilters,
        generatedAt: generatedAtFilters,
      };
    });
  }, [debouncedKeyPhrase, filterGroups]);

  const [articlesResponse] = useQuery({
    query: AdminArticlesQuery,
    requestPolicy: "cache-and-network",
    variables: {
      filters: queryFilter,
      paginationOptions: {
        take: pagination.pageSize,
        skip: pagination.pageIndex * pagination.pageSize,
      },
      sort: articleSort,
    },
  });

  const tags =
    articlesResponse.data?.tags
      ?.filter((tag): tag is typeof tag & { name: string } => !!tag.name)
      ?.map((tag) => ({
        value: tag.id,
        label: tag.name,
      })) ?? [];

  const articles = useMemo(
    () =>
      articlesResponse.data?.adminArticles.nodes.map((node) =>
        getFragmentData(AdminArticleFragment, node),
      ) ?? [],
    [articlesResponse.data?.adminArticles.nodes],
  );

  const columnHelper = createColumnHelper<(typeof articles)[number]>();

  const columns = [
    columnHelper.accessor("title", {
      header: "Article title",
      cell: ArticleCell,
      minSize: 300,
      maxSize: 600,
      meta: {
        sticky: true,
      },
    }),
    columnHelper.accessor("tenant", {
      header: "Team",
      size: 280,
      cell: ({ cell }) => cell.getValue()?.name ?? "-",
    }),
    columnHelper.accessor("keyword", {
      header: "Keyword",
      size: 280,
      cell: KeywordCell,
    }),
    columnHelper.accessor("status", {
      header: "Status",
      size: 198,
      cell: StatusCell,
    }),
    columnHelper.accessor("createdBy", {
      header: "Created by",
      size: 140,
      cell: UserCell,
    }),
    columnHelper.accessor("createdAt", {
      header: "Created",
      size: 140,
      cell: ({ cell }) => <FormattedDate date={cell.getValue()} />,
    }),
    ...(selectedTableView === "full"
      ? [
          columnHelper.accessor("generatedAt", {
            header: "Generated",
            size: 140,
            cell: ({ cell }) => {
              const value = cell.getValue();

              return value ? <FormattedDate date={value} /> : "-";
            },
          }),
          columnHelper.accessor("updatedAt", {
            header: "Updated",
            size: 140,
            cell: ({ cell }) => <FormattedDate date={cell.getValue()} />,
          }),
          columnHelper.accessor("publishedAt", {
            header: "Published",
            size: 140,
            cell: ({ cell }) => {
              const value = cell.getValue();

              return value ? <FormattedDate date={value} /> : "-";
            },
          }),
          columnHelper.accessor("tag", {
            header: "Tag",
            size: 212,
            cell: ({ cell }) => cell.getValue()?.name ?? "-",
          }),
          columnHelper.accessor("metaDescription", {
            header: "Meta description",
            size: 212,
            cell: ({ cell }) => <Truncate>{cell.getValue() ?? "-"}</Truncate>,
          }),
          columnHelper.accessor("excerpt", {
            header: "Excerpt",
            size: 212,
            cell: ({ cell }) => <Truncate>{cell.getValue() ?? "-"}</Truncate>,
          }),
          columnHelper.accessor("language", {
            header: "Language",
            size: 212,
            cell: ({ cell }) => {
              const value = cell.getValue();

              if (value === "english-us") {
                return "English (US)";
              }

              if (value === "english-uk") {
                return "English (UK)";
              }

              return value ? toSentenceCase(value) : "-";
            },
          }),
          columnHelper.accessor("additionalPrompt", {
            header: "Custom prompt",
            size: 212,
            cell: ({ cell }) => <Truncate>{cell.getValue() ?? "-"}</Truncate>,
          }),
        ]
      : []),
  ];

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPagination(DEFAULT_PAGINATION_STATE);
      setDebouncedKeyPhrase(keyPhrase);
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [keyPhrase]);

  const onSortingChange = useCallback((sortState: SortingState) => {
    const sort = sortState[0];

    if (!sort) {
      setArticleSort(undefined);
      return;
    }

    const field = sort.id;
    const direction = sort.desc ? SortOrder.Desc : SortOrder.Asc;

    if (Object.values(ArticleSortField).includes(field as ArticleSortField)) {
      setArticleSort({
        field: field as ArticleSortField,
        direction,
      });
    } else {
      setArticleSort(undefined);
    }
  }, []);

  return (
    <div className={styles.container}>
      <Table
        columns={columns}
        data={articles}
        isSelectable
        Header={
          <div className={styles.header}>
            <TextInput
              leftSection={<Search size="medium" />}
              onChange={(event) => {
                setKeyPhrase(event.currentTarget.value);
              }}
              placeholder="Search articles"
              value={keyPhrase}
              className={styles.search}
            />
            <FilterButton
              filterCount={filterGroups.reduce(
                (count, filter) => (count += filter.length),
                0,
              )}
              open={open}
              clearFilters={clearFilters}
            />

            <ToggleGroup
              selectedItem={selectedTableView}
              setSelectedItem={setSelectedTableView}
              className={styles.buttonGroup}
              items={[
                { id: "simple", name: "Simple Table" },
                { id: "full", name: "Full Table" },
              ]}
            />
            <FilterDrawer
              filterGroups={filterGroups}
              configuration={[
                {
                  title: "Processing",
                  configurations: [
                    {
                      name: "status",
                      label: "Status",
                      type: "enum",
                      values: Object.values(ArticleStatus).map((status) => ({
                        value: status,
                        label: status,
                      })),
                    },
                  ],
                },
                {
                  title: "Content",
                  configurations: [
                    { name: "keyword", label: "Keyword", type: "string" },
                    { name: "title", label: "Title", type: "string" },
                    { name: "body", label: "Body", type: "string" },
                    { name: "tag", label: "Tag", type: "enum", values: tags },
                    {
                      name: "metaDescription",
                      label: "Meta description",
                      type: "string",
                    },
                    { name: "excerpt", label: "Excerpt", type: "string" },
                    {
                      name: "additionalPrompt",
                      label: "Custom prompt",
                      type: "string",
                    },
                  ],
                },
                {
                  title: "Dates",
                  configurations: [
                    {
                      type: "date",
                      label: "Created Date",
                      name: "createdAt",
                    },
                    {
                      type: "date",
                      label: "Generated Date",
                      name: "generatedAt",
                    },
                    {
                      type: "date",
                      label: "Updated Date",
                      name: "updatedAt",
                    },
                  ],
                },
              ]}
              isOpen={isFiltersDrawerOpen}
              onClose={close}
              addFilter={addFilter}
              editFilter={editFilter}
              deleteFilter={deleteFilter}
            />
          </div>
        }
        loading={articlesResponse.fetching}
        pagination={pagination}
        setPagination={setPagination}
        totalCount={articlesResponse.data?.adminArticles.totalCount}
        resourceName={{ plural: "articles", singular: "article" }}
        onSortingChange={onSortingChange}
        enableSorting
        isFiltered={!!queryFilter.length}
      />
    </div>
  );
}

export default AdminArticles;
